import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, CardContent } from '@mui/material';
import * as turf from "@turf/turf";

import MarkerMap from "../../assets/images/marker-map.png";

import useMap from "../../hooks/mapbox/useMap";
import environment from '../../environment/environment';
import useMarker from '../../hooks/mapbox/useMarker';
import useHeatmap from '../../hooks/mapbox/useHeatmap';
import { mapBox } from "../../common/staticVariables";
import moment from "moment";

const GeoLocationTrackingMap = ({regionsContainerRef, verificationData, viewHeatmap, geolocationData}) => {

    const mapCardContainerRef = useRef();
    const mapContainerRef = useRef();
    const mapRef = useRef();

    const [regionsContainerHeight, setSetRegionsContainerHeight] = useState(null);

    const {map, mapLoaded, initMap, addSource, getSource} = useMap(mapRef);
    const {addMarkersWithLayer, getMarkersWithLayer} = useMarker(map)
    const {addHeatmapWithCircleLayer, getHeatmapWithCircleLayer} = useHeatmap(map)
    
    // Set initial map height
    useEffect(() => {
        if (regionsContainerRef.current) {
            const computedStyle = window.getComputedStyle(mapCardContainerRef.current);
            let height_diff = parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom)
            setSetRegionsContainerHeight(regionsContainerRef.current.clientHeight - height_diff)
        }
    }, [regionsContainerRef, mapCardContainerRef])

    // Render initial map
    useEffect(() => {
        if (regionsContainerHeight) {
            if (!mapLoaded) {
                let mapOpts = {
                    token: environment.mapBox.mapBoxToken,
                    containerRef: mapContainerRef.current,
                    style: 'mapbox://styles/mapbox/streets-v12',
                    center: [-24, 42],
                    zoom: 1
                }
                initMap(mapOpts)
            }
        }
    }, [regionsContainerHeight, mapLoaded]);

    // handle map
    useEffect(() => {
        if (mapLoaded) {
            const heatmap_layer_id = mapBox.layer.heatmap_verification_layer
            const circle_layer_id = mapBox.layer.circle_verification_layer
            const heatmap_with_circle_layer = getHeatmapWithCircleLayer(heatmap_layer_id, circle_layer_id)

            const markers_layer_id = mapBox.layer.marker_verification_layer
            const markers_layer = getMarkersWithLayer(markers_layer_id)

            // handle remove layers if no records.
            if (verificationData.length == 0) {
                if (viewHeatmap) {
                    if (heatmap_with_circle_layer) heatmap_with_circle_layer.removeHeatmap()
                } else {
                    if (markers_layer) markers_layer.removeMarkers()
                }
                return;
            }

            // // handle create GeoJSON for mapbox API
            const center_camera = getCenterCamera(verificationData)
            const geojson = getGeoJSON(verificationData, geolocationData)

            // handle toggle layer
            if (viewHeatmap) {
                if (markers_layer) markers_layer.removeMarkers()
                handleLoadHeatMap(heatmap_layer_id, circle_layer_id, heatmap_with_circle_layer, geojson, center_camera)
            } else {
                if (heatmap_with_circle_layer) heatmap_with_circle_layer.removeHeatmap()
                handleLoadMarkersToMap(markers_layer_id, markers_layer, geojson, center_camera)
            }
        }
    }, [verificationData, mapLoaded, viewHeatmap])

    const getCenterCamera = (data) => {
        console.log("getCenterCamera data 11", data);

        const transformedCoordinates = data.map(({ longitude: lng, latitude: lat }) => [lng, lat]);
        console.log("transformedCoordinates 11", transformedCoordinates);

        let centerCamera;
        
        const polygon = {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [
                    transformedCoordinates
                ]
            }
        };

        // For center focus camera map
        if (transformedCoordinates.length == 1) {
            polygon.geometry.coordinates = transformedCoordinates[0];
            centerCamera = polygon;
        } else {
            centerCamera = turf.centroid(polygon);
        }

        return centerCamera
    }

    const getGeoJSON = (data, geolocationData) => {
        let mapBoxFeatures = data.map((item) => {

            let statusColor = item.status === "Suspicious" ? "#fda501" : item.status === "Successful" ? "#3fbf7e" : "#e67260";
            let formattedTimestamp = moment(item.timestamp).format("MMMM DD, YYYY h:mm:ss A");

            let data = {
                type: 'Feature',
                properties: {
                    description: `
                        <div class="custom-popup">
                            <p><b>OVI: </b>${item.ovi}</p>
                            <p><b>Device: </b>${item.device_identity}</p>
                            <p><b>Latitude: </b>${item.latitude}</p>
                            <p><b>Region: </b>${geolocationData[0].region}</p>
                            <p><b>Status: </b><span style="color: ${statusColor};">${item.status}</span></p>
                            <p><b>Total Verification: </b>${geolocationData[0].count}</p>
                            <p><b>Timestamp: </b>${formattedTimestamp}</p>
                        </div>
                    `
                },
                geometry: {
                    type: 'Point',
                    coordinates: [item.longitude, item.latitude]
                }
            }
            return data
        })

        const mapBoxCustomData = {
            type: 'FeatureCollection',
            features: mapBoxFeatures
        }

        return mapBoxCustomData
    }

    const handleLoadMarkersToMap = (markers_layer_id, markers_layer, geojson, centerCamera) => {
        let marker_camera_options = {
            center: centerCamera.geometry.coordinates,
            zoom: 6.8,
        }

        if (!markers_layer) {
            // Setup source
            let source_id = mapBox.source.verification_source
            let source = getSource(source_id);

            if (!source) {
                let source_obj = {
                    type: 'geojson',
                    data: geojson
                }
                addSource(source_id, source_obj)
            }

            // Setup marker with layer
            let img_marker_id = mapBox.marker.icon_img_id

            let marker_layer_options = {
                id: markers_layer_id,
                icon_img_id: img_marker_id,
                icon_src: MarkerMap,
                size: 0.3,
                popup: {
                    isHover: true,
                }
            }

            addMarkersWithLayer(source_id, marker_layer_options, marker_camera_options)
        } else {
            // sample of update markers
            let updated_props = {
                camera: marker_camera_options
            }
            markers_layer.updateMarkers(updated_props)
            markers_layer.updateData(geojson)
        }
    }

    const handleLoadHeatMap = (heatmap_layer_id, circle_layer_id, heatmap_with_circle_layer, geojson, centerCamera) => {
        console.log("geojson bbb", geojson);
        
        if (!heatmap_with_circle_layer) {
            // Setup source
            let source_id = mapBox.source.verification_source
            let source = getSource(source_id);
            if (!source) {
                let source_obj = {
                    type: 'geojson',
                    data: geojson
                }
                addSource(source_id, source_obj)
            }

            let heatmap_layer_options = {
                id: heatmap_layer_id,
                heatmap_colors: ['green', 'yellow', 'orange', 'red'],
            }
    
            let circle_layer_options = {
                id: circle_layer_id,
            }
    
            let heatmap_camera_options = {
                center: centerCamera.geometry.coordinates,
                zoom: 6.8,
            }

            addHeatmapWithCircleLayer(source_id, heatmap_layer_options, circle_layer_options, heatmap_camera_options)
        } else {
            heatmap_with_circle_layer.updateData(geojson)
        }
    }

    return (
        <Card variant="outlined">
            <CardContent ref={mapCardContainerRef}>
                {regionsContainerHeight ? (
                    <Box
                        id="map"
                        ref={mapContainerRef}
                        sx={{
                            height: regionsContainerHeight,
                            minHeight: 300, 
                            backgroundColor: "#f0f0f0", 
                            border: "1px solid #ccc", 
                            borderRadius: "8px", 
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            height: "300px", 
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f0f0f0",
                        }}
                    >
                        <p>Loading map...</p> 
                    </Box>
                )}
            </CardContent>
        </Card>
    )
}

export default GeoLocationTrackingMap